import {
    Box,
    Checkbox,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableRow,
    Typography,
} from '@mui/material';
import { Fragment } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { Separated } from '~/components/Separated';
import { formatDayText } from '~/containers/patient-list/model';
import ModalityChip from '~/components/Chip/ModalityChip';
import { dicomDateToIso } from '~/domain/dicom/importScan';
import {
    groupRemoteSeriesByStudy,
    type RemoteStudyGroup,
    type SourcedSeriesRow,
} from '../model';
import {
    PacsBodyCell,
    PacsColgroup,
    PacsGroupCell,
    PacsCompactRow,
    PACS_TABLE_SX,
    type PacsColumn,
} from './pacsTable';

/**
 * The series a retrieval can request, under the study each belongs to.
 *
 * The study is structure here, not decoration. Modality, description and instance count are all a
 * series row carries, and two planning CTs of one patient can agree on all three, so a flat list
 * would leave a superseded timepoint indistinguishable from the current one at the moment of
 * commit.
 *
 * There is no head row. A chip labelled CT does not need a column called Modality above it, and the
 * one label that was doing work rides on its own value instead, so the study bands are the only
 * bands in the list. Two pinned bars of the same height read as one two-line header whatever their
 * ink, and the study date is the thing this screen cannot afford to have mistaken for chrome.
 *
 * When more than one source was read, every row names the source it will be retrieved from. A
 * series held by several sources offers the choice in place; one held by a single source states it.
 * The source is never left implicit: the same series in a planning system and an archive can differ
 * in what was last edited, and which one lands is part of what the user is committing to.
 */

type PacsSeriesTableProps = {
    rows: readonly SourcedSeriesRow[];
    selected: ReadonlySet<string>;
    /** The source each series will be retrieved from. */
    retrieveFrom: ReadonlyMap<string, string>;
    /** True when the list was read from more than one source, so rows name theirs. */
    showSources: boolean;
    /** True once the retrievals are being issued; the reducer refuses edits from then on. */
    disabled: boolean;
    onToggle: (key: string) => void;
    onToggleGroup: (keys: readonly string[], selected: boolean) => void;
    onChooseSource: (key: string, source: string) => void;
};

/** Tracks only. Nothing here is labelled, so the widths are all a column has to say. */
const COLUMNS: readonly PacsColumn[] = [
    { id: 'select', width: 20 },
    { id: 'modality', width: 76 },
    { id: 'series', width: 'flex' },
    { id: 'instances', width: 72 },
];

const SOURCE_COLUMN: PacsColumn = { id: 'source', width: 120 };

/**
 * When the study was taken. Mono only when there is a date: the family is what marks a figure, so
 * setting the "no date reported" sentence in it would read as a value the remote sent.
 */
function studyDay(
    intl: IntlShape,
    group: RemoteStudyGroup
): { label: string; isDate: boolean } {
    const day = formatDayText(intl, dicomDateToIso(group.studyDate));
    return day
        ? { label: day, isDate: true }
        : {
              label: intl.formatMessage({
                  id: 'patientList.import.pacs.undatedStudy',
              }),
              isDate: false,
          };
}

export default function PacsSeriesTable({
    rows,
    selected,
    retrieveFrom,
    showSources,
    disabled,
    onToggle,
    onToggleGroup,
    onChooseSource,
}: PacsSeriesTableProps) {
    const groups = groupRemoteSeriesByStudy(rows);
    const columns = showSources ? [...COLUMNS, SOURCE_COLUMN] : COLUMNS;
    return (
        // Separated borders rather than collapsed: a sticky cell needs them, and without a head row
        // there is no `stickyHeader` to set them for us.
        <Table
            size="small"
            sx={{
                ...PACS_TABLE_SX,
                borderCollapse: 'separate',
                borderSpacing: 0,
            }}
        >
            <PacsColgroup columns={columns} />
            <TableBody>
                {groups.map((group) => (
                    <Fragment key={group.studyInstanceUid}>
                        <StudyBand
                            group={group}
                            span={columns.length}
                            selected={selected}
                            disabled={disabled}
                            onToggleGroup={onToggleGroup}
                        />
                        {group.rows.map((row) => (
                            <SeriesRow
                                key={row.key}
                                row={row}
                                checked={selected.has(row.key)}
                                source={
                                    showSources
                                        ? retrieveFrom.get(row.key)
                                        : undefined
                                }
                                disabled={disabled}
                                onToggle={() => onToggle(row.key)}
                                onChooseSource={(source) =>
                                    onChooseSource(row.key, source)
                                }
                            />
                        ))}
                    </Fragment>
                ))}
            </TableBody>
        </Table>
    );
}

function StudyBand({
    group,
    span,
    selected,
    disabled,
    onToggleGroup,
}: {
    group: RemoteStudyGroup<SourcedSeriesRow>;
    span: number;
    selected: ReadonlySet<string>;
    disabled: boolean;
    onToggleGroup: (keys: readonly string[], selected: boolean) => void;
}) {
    const intl = useIntl();
    const keys = group.rows.map((row) => row.key);
    const chosen = keys.filter((key) => selected.has(key)).length;
    const day = studyDay(intl, group);
    return (
        <TableRow>
            <PacsGroupCell colSpan={span}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <Checkbox
                        checked={chosen === keys.length}
                        indeterminate={chosen > 0 && chosen < keys.length}
                        disabled={disabled}
                        onChange={() =>
                            onToggleGroup(keys, chosen < keys.length)
                        }
                        size="small"
                        sx={{ p: 0, width: 20 }}
                        inputProps={{
                            'aria-label': intl.formatMessage(
                                {
                                    id: 'patientList.import.pacs.selectStudy',
                                },
                                { study: day.label }
                            ),
                        }}
                    />
                    {/* One segment list, so an unreported description leaves no dangling separator.
                        The tally rides in it rather than at the far edge: right-aligned it lands in
                        the Instances track and pairs with the column label above into a header. */}
                    <Separated>
                        <Box
                            component="span"
                            sx={
                                day.isDate
                                    ? { fontFamily: 'fontFamilyMonospace' }
                                    : { color: 'action.disabled' }
                            }
                        >
                            {day.label}
                        </Box>
                        {group.studyDescription ? (
                            <Box
                                component="span"
                                sx={{ color: 'text.secondary' }}
                            >
                                {group.studyDescription}
                            </Box>
                        ) : undefined}
                        <Box
                            component="span"
                            sx={{
                                fontFamily: 'fontFamilyMonospace',
                                color: 'action.disabled',
                            }}
                        >
                            {intl.formatMessage(
                                { id: 'patientList.import.pacs.seriesCount' },
                                { count: group.rows.length }
                            )}
                        </Box>
                    </Separated>
                </Box>
            </PacsGroupCell>
        </TableRow>
    );
}

function SeriesRow({
    row,
    checked,
    source,
    disabled,
    onToggle,
    onChooseSource,
}: {
    row: SourcedSeriesRow;
    checked: boolean;
    /** The source it will be retrieved from; absent when rows do not name one. */
    source: string | undefined;
    disabled: boolean;
    onToggle: () => void;
    onChooseSource: (source: string) => void;
}) {
    const intl = useIntl();
    const label =
        row.seriesDescription ||
        intl.formatMessage({ id: 'patientList.import.pacs.untitledSeries' });
    return (
        <PacsCompactRow
            isSelected={false}
            onClick={disabled ? undefined : onToggle}
            sx={{ cursor: disabled ? 'default' : 'pointer' }}
        >
            <PacsBodyCell>
                <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={onToggle}
                    // The row carries the press; the checkbox must not turn the
                    // selection back off by taking the same click a second time.
                    onClick={(event) => event.stopPropagation()}
                    size="small"
                    sx={{ p: 0, width: 20 }}
                    inputProps={{
                        'aria-label': intl.formatMessage(
                            { id: 'patientList.import.pacs.selectSeries' },
                            { series: label }
                        ),
                    }}
                />
            </PacsBodyCell>
            <PacsBodyCell>
                <ModalityChip modality={row.modality ?? '?'} />
            </PacsBodyCell>
            <PacsBodyCell>
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        display: 'block',
                        minWidth: 0,
                        fontSize: 13,
                        color: disabled ? 'action.disabled' : 'text.primary',
                    }}
                >
                    {label}
                </Typography>
            </PacsBodyCell>
            {/* Carries its own unit, there being no column header to name it. Blank rather than
                zero: a remote that reported no count has not told us a total. */}
            <PacsBodyCell>
                <Typography variant="monoMeta">
                    {row.instanceCount === undefined
                        ? ''
                        : intl.formatMessage(
                              {
                                  id: 'patientList.import.pacs.instanceCount',
                              },
                              { count: row.instanceCount }
                          )}
                </Typography>
            </PacsBodyCell>
            {source !== undefined && (
                // The source picker takes its own clicks; the row's press is the selection toggle.
                <PacsBodyCell onClick={(event) => event.stopPropagation()}>
                    <SeriesSource
                        row={row}
                        label={label}
                        source={source}
                        disabled={disabled}
                        onChoose={onChooseSource}
                    />
                </PacsBodyCell>
            )}
        </PacsCompactRow>
    );
}

/**
 * Where one series will be retrieved from. A choice when several sources hold it, a plain statement
 * when only one does: a picker with a single option would suggest there was something to decide.
 */
function SeriesSource({
    row,
    label,
    source,
    disabled,
    onChoose,
}: {
    row: SourcedSeriesRow;
    label: string;
    source: string;
    disabled: boolean;
    onChoose: (source: string) => void;
}) {
    const intl = useIntl();
    if (row.sources.length < 2) {
        return (
            <Typography
                component="span"
                noWrap
                sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}
            >
                {source}
            </Typography>
        );
    }
    return (
        <Select
            value={source}
            onChange={(event) => onChoose(event.target.value)}
            disabled={disabled}
            size="small"
            variant="standard"
            disableUnderline
            SelectDisplayProps={{
                'aria-label': intl.formatMessage(
                    { id: 'patientList.import.pacs.retrieveSeriesFrom' },
                    { series: label }
                ),
            }}
            sx={{ width: '100%', fontSize: 12 }}
        >
            {row.sources.map((holder) => (
                <MenuItem key={holder} value={holder} sx={{ fontSize: 12 }}>
                    {holder}
                </MenuItem>
            ))}
        </Select>
    );
}
